import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import PropTypes from 'prop-types';

import { reconcileRelayFax } from 'store/thunks/workQueueThunks';

import { relayFaxDestination } from '../constants/nonRoutableRequests';
import { useNonRoutableResolution } from '../hooks/useNonRoutableResolution';
import { nonRoutableRetryIsSafe } from '../utils/nonRoutableRequestErrors';
import NonRoutableRequestIdentity from './NonRoutableRequestIdentity';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';
import RelayFaxPhoneConfirmation from './RelayFaxPhoneConfirmation';

const successMessage = () =>
  'Reconciled. The order is marked delivered and now appears in the Relay tab.';

/**
 * RECONCILE A PARKED RELAY FAX (POM-6279): the operator confirmed BY PHONE that the pharmacy has
 * the fax. The relay order moves to TRANSFERRED and the request resolves RECONCILED.
 *
 * The server offers it only when something was transmitted (delivery failed, or status unknown) or
 * a resend's claim went stale — never after a failure that sent nothing, because then no pharmacy
 * can have it. The button is still gated on the operator saying what the pharmacy told them.
 */
const ReconcileRelayFaxDialog = ({ open, onClose, item }) => {
  const [phoned, setPhoned] = useState(false);
  const { submit, submitting, outcome, dismiss } = useNonRoutableResolution({
    item,
    action: reconcileRelayFax,
    successMessage,
    onClose,
    open,
  });

  useEffect(() => {
    if (open) setPhoned(false);
  }, [open]);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    dismiss();
  };

  const canSubmit = !outcome || nonRoutableRetryIsSafe(outcome.described);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='ReconcileRelayFaxDialog-title'
    >
      <DialogTitle id='ReconcileRelayFaxDialog-title'>Mark this fax as received?</DialogTitle>
      <DialogContent>
        <NonRoutableRequestIdentity item={item} />

        <Alert severity='warning'>
          <AlertTitle>Only after the pharmacy confirms by phone</AlertTitle>
          {`Phone ${relayFaxDestination(item)} and confirm they received this fax. Reconciling ` +
            'marks the order delivered: it leaves this queue and appears in the Relay tab. This ' +
            'cannot be undone.'}
          {canSubmit && (
            <RelayFaxPhoneConfirmation
              id={`ReconcileRelayFaxDialog-${item.nonRoutableRequestId}-phoned`}
              checked={phoned}
              onChange={setPhoned}
              label='I phoned the pharmacy and they confirmed they received this fax.'
            />
          )}
        </Alert>

        {outcome && <NonRoutableResolutionOutcome outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={dismiss} disabled={submitting}>
          {outcome && !canSubmit ? 'Close' : 'Leave it parked'}
        </Button>
        {canSubmit && (
          <LoadingButton
            variant='contained'
            color='primary'
            loading={submitting}
            disabled={!phoned}
            onClick={submit}
            id={`ReconcileRelayFaxDialog-${item.nonRoutableRequestId}-confirm`}
          >
            Reconcile Fax
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

ReconcileRelayFaxDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    destinationName: PropTypes.string,
  }).isRequired,
};

export default ReconcileRelayFaxDialog;
